import type { ReactNode } from 'react';
import styled from '@emotion/styled';

/* ── types ── */

interface Headshot {
	medium_1x: string;
	medium_2x?: string;
}

interface Profile {
	name: string;
	headshot?: Headshot;
	socialLinks?: ReactNode;
	logo?: string;
	organization?: string;
	title?: string;
	company?: string;
	phone?: string;
	email?: string;
	office?: string;
	website?: string;
	domain?: string;
	license?: string;
}

/* ── styled components ── */

const Section = styled.section`
	background-color: white;
	color: black;
	display: flex;
	flex-direction: column;
	align-items: center;
	padding-top: 1.5rem;
	padding-bottom: 1.5rem;
`;

const Heading = styled.div`
	text-transform: uppercase;
	font-weight: 500;
	font-size: 1.5rem;
	line-height: 2rem;
	letter-spacing: 0.3em;
	text-align: center;
	padding-top: 1.5rem;
	padding-bottom: 1.5rem;
	border-bottom: 10px solid #e5e7eb;
	width: 100%;
	@media (min-width: 768px) {
		font-size: 2.25rem;
		line-height: 2.5rem;
	}
`;

const ProfileList = styled.div`
	max-width: 1536px;
	margin-left: auto;
	margin-right: auto;
	display: flex;
	flex-direction: column;
	& > * + * {
		margin-top: 1.5rem;
	}
	padding-top: 3rem;
	padding-bottom: 2rem;
	@media (max-width: 639px) {
		align-items: center;
	}
`;

const ProfileListInner = styled.div`
	& > * + * {
		margin-top: 1.5rem;
	}
	@media (min-width: 1024px) {
		& > * + * {
			margin-top: 3rem;
		}
	}
`;

const ProfileCard = styled.div`
	display: flex;
	border-bottom: 1px solid #d1d5db;
	padding-bottom: 1.5rem;
	&:last-child {
		border-bottom: 0;
		padding-bottom: 0;
	}
	@media (max-width: 639px) {
		flex-direction: column;
		& > * + * {
			margin-top: 1.5rem;
		}
	}
	@media (min-width: 640px) {
		& > * + * {
			margin-left: 1.5rem;
		}
	}
	@media (min-width: 1024px) {
		padding-left: 3rem;
		padding-right: 3rem;
		padding-bottom: 3rem;
		&:last-child {
			padding-bottom: 0;
		}
		& > * + * {
			margin-left: 3rem;
		}
	}
`;

const Sidebar = styled.div`
	width: 12rem;
	display: flex;
	flex-direction: column;
	gap: 1.5rem;
`;

const HeadshotImg = styled.img`
	width: 12rem;
	height: 12rem;
	object-fit: cover;
`;

const InfoColumn = styled.div`
	& > * + * {
		margin-top: 1rem;
	}
	@media (max-width: 639px) {
		text-align: center;
	}
`;

const LogoRow = styled.div`
	@media (max-width: 639px) {
		text-align: center;
		& > * + * {
			margin-top: 1rem;
		}
	}
	@media (min-width: 640px) {
		display: flex;
		justify-items: center;
		align-items: center;
	}
`;

const LogoImg = styled.img`
	height: fit-content;
	max-width: 24rem;
	max-height: 3rem;
	object-fit: cover;
	@media (min-width: 1024px) {
		max-height: 6rem;
	}
`;

const Name = styled.div`
	font-size: 1.25rem;
	line-height: 1.75rem;
	@media (min-width: 1024px) {
		font-size: 2.25rem;
		line-height: 2.5rem;
	}
`;

const Organization = styled.span`
	font-size: 1rem;
	line-height: 1.5rem;
	color: #4b5563;
	@media (min-width: 1024px) {
		font-size: 1.25rem;
		line-height: 1.75rem;
	}
`;

const DetailsGrid = styled.div`
	display: flex;
	flex-direction: column;
	gap: 1.5rem;
	font-size: 0.875rem;
	line-height: 1.25rem;
	@media (min-width: 640px) {
		flex-direction: row;
		flex-wrap: wrap;
		& > * + * {
			margin-left: 4rem;
		}
		row-gap: 1.25rem;
	}
`;

const DetailItem = styled.div`
	@media (min-width: 768px) {
		& > * + * {
			margin-top: 0.25rem;
		}
	}
`;

const DetailLabel = styled.div`
	text-transform: uppercase;
	font-weight: 700;
	color: #4b5563;
	letter-spacing: 0.1em;
`;

const DetailLink = styled.a`
	padding-bottom: 0.5rem;
	border-bottom: 1px solid black;
	&:hover {
		color: #4b5563;
	}
`;

/* ── component ── */

export default function ProfileBlockEta({ profiles }: { profiles: Profile[] }) {
	if (profiles.length === 0) return null;

	return (
		<Section id="details">
			<Heading>Presented By</Heading>
			<ProfileList>
				<ProfileListInner>
					{profiles.map((profile, i) => (
						<ProfileCard key={i}>
							<Sidebar>
								{profile.headshot?.medium_1x && (
									<HeadshotImg
										src={profile.headshot.medium_1x}
										srcSet={profile.headshot.medium_2x ? `${profile.headshot.medium_1x}, ${profile.headshot.medium_2x} 2x` : undefined}
									/>
								)}
								{profile.socialLinks}
							</Sidebar>

							<InfoColumn>
								<LogoRow>{profile.logo && <LogoImg alt={profile.organization ?? ''} src={profile.logo} />}</LogoRow>

								<Name>{profile.name}</Name>
								{profile.organization && <Organization>{profile.organization}</Organization>}

								<DetailsGrid>
									{profile.title && (
										<DetailItem>
											<DetailLabel>Title</DetailLabel>
											<div>{profile.title}</div>
										</DetailItem>
									)}
									{profile.company && (
										<DetailItem>
											<DetailLabel>Company</DetailLabel>
											<div>{profile.company}</div>
										</DetailItem>
									)}
									{profile.phone && (
										<DetailItem>
											<DetailLabel>Phone</DetailLabel>
											<div>{profile.phone}</div>
										</DetailItem>
									)}
									{profile.email && (
										<DetailItem>
											<DetailLabel>Email</DetailLabel>
											<DetailLink href={`mailto:${profile.email}`}>{profile.email}</DetailLink>
										</DetailItem>
									)}
									{profile.office && (
										<DetailItem>
											<DetailLabel>Office</DetailLabel>
											<div>{profile.office}</div>
										</DetailItem>
									)}
									{profile.website && (
										<DetailItem>
											<DetailLabel>Web</DetailLabel>
											<DetailLink href={profile.website}>{profile.domain}</DetailLink>
										</DetailItem>
									)}
									{profile.license && (
										<DetailItem>
											<DetailLabel>License #</DetailLabel>
											<span>{profile.license}</span>
										</DetailItem>
									)}
								</DetailsGrid>
							</InfoColumn>
						</ProfileCard>
					))}
				</ProfileListInner>
			</ProfileList>
		</Section>
	);
}
